import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Clock, DollarSign, ExternalLink, Github, FileText, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import MetaTags from '../components/SEO/MetaTags';
import { pageConfigs } from '../utils/seo';

const JobApplication: React.FC = () => {
  return (
    <>
      <MetaTags {...pageConfigs.careers} />

      <div className="min-h-screen bg-gradient-to-br from-[#0a1a1a] via-[#1a2f2f] to-[#2d4a4a]">
        {/* Header */}
        <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Back Button */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <Link
                to="/careers"
                className="inline-flex items-center gap-2 text-[#58ACDD] hover:text-white transition-colors duration-300"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Back to Careers</span>
              </Link>
            </motion.div>

            {/* Job Header */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-8"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-productSans">
                Computer Vision Engineer / ML Specialist
              </h1>

              <div className="flex flex-wrap gap-6 text-white/70 mb-6">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm">Remote</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">4-6 Month Contract [Part Time] - Potential For Hire</span>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm">$4-6K (Duration of the Contract)</span>
                </div>
              </div>

            </motion.div>
          </div>
        </section>

        {/* Job Content */}
        <section className="px-4 sm:px-6 lg:px-8 pb-20">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12 space-y-8"
            >
              {/* About FOWND */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-productSans">About FOWND</h2>
                <p className="text-white/80 leading-relaxed font-productSans">
                  FOWND is founded by physical therapists to solve real-world clinical challenges through AI. Our mission is to reduce the administrative burden of healthcare providers by delivering intelligent, compliant documentation and visual understanding tools for therapy sessions. By combining clinical expertise with cutting-edge AI, we are building clinician-in-the-loop systems that enhance both efficiency and patient care.
                </p>
              </div>

              {/* Role Overview */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-productSans">Role Overview</h2>
                <p className="text-white/80 leading-relaxed font-productSans">
                  We are seeking a passionate <strong className="text-[#58ACDD]">Computer Vision Engineer</strong> (open to both recent graduates and experienced candidates) to design and implement models that power our AI-driven clinical documentation tools. This role offers the unique opportunity to apply advanced computer vision and machine learning techniques directly to healthcare, with the potential for your work to be deployed in clinical settings.
                </p>
              </div>

              {/* Key Responsibilities */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-productSans">Key Responsibilities</h2>
                <ul className="space-y-3 text-white/80 font-productSans">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Design, develop, and optimize computer vision and machine learning models with applications in healthcare.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Implement algorithms for classification, object detection, segmentation, tracking, and pose estimation.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Build, preprocess, and augment datasets from video and visual data.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Integrate learning pipelines into FOWND's platform, ensuring performance meets clinical needs (accuracy, latency, reliability).</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Collaborate with data scientists, engineers, and physical therapists to validate model outputs in real-world use cases.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Deploy and scale models into production environments, including potential edge-device optimization.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Stay at the forefront of computer vision and AI by researching and applying the latest advancements.</span>
                  </li>
                </ul>
              </div>

              {/* Qualifications */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-productSans">Qualifications</h2>
                <ul className="space-y-3 text-white/80 font-productSans">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#486633] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Bachelor's or Master's degree in Computer Science, Electrical/Computer Engineering, Computer Vision, Applied Math, Machine Learning, or a related field.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#486633] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Strong programming skills in Python and familiarity with libraries such as PyTorch, TensorFlow, OpenCV, NumPy, and scikit-learn.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#486633] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Solid understanding of machine learning fundamentals and hands-on experience with computer vision algorithms.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#486633] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Experience working with video-based data, including preprocessing and annotation.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#486633] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Strong debugging and problem-solving skills, with attention to practical edge cases.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#486633] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Ability to collaborate across technical and non-technical teams, including healthcare professionals.</span>
                  </li>
                </ul>
              </div>

              {/* Nice-to-Have */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-productSans">Nice-to-Have</h2>
                <ul className="space-y-3 text-white/80 font-productSans">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-white/40 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Experience with healthcare datasets or medical imaging.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-white/40 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Knowledge of edge deployment (TensorRT, ONNX, mobile optimization).</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-white/40 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Contributions to open-source projects, published research, or practical projects in computer vision.</span>
                  </li>
                </ul>
              </div>

              {/* What You'll Gain */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-productSans">What You'll Gain</h2>
                <ul className="space-y-3 text-white/80 font-productSans">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Direct impact by applying AI to meaningful healthcare problems.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Mentorship and collaboration with engineers and clinicians.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Experience translating research into production-ready applications.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                    <span>The opportunity to see your work in real clinical workflows, shaping how AI supports patient care.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                    <span>A mission-driven, startup environment where innovation directly improves lives.</span>
                  </li>
                </ul>
              </div>

              {/* Application Form */}
              <div className="bg-gradient-to-r from-[#58ACDD]/10 to-[#486633]/10 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-6 font-productSans">Apply Now</h2>
                <p className="text-white/80 mb-6 font-productSans">
                  Ready to make an impact? Submit your application below and we'll be in touch soon.
                </p>
                
                {/* Airtable Form Embed */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2 border border-white/20">
                  <iframe 
                    className="airtable-embed w-full rounded-lg" 
                    src="https://airtable.com/embed/appgypC2UkBHPRSYU/pag8m0Yfmp2taCwDP/form" 
                    frameBorder="0" 
                    width="100%" 
                    height="800" 
                    style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', minHeight: '800px' }}
                  />
                </div>
                
                <p className="text-white/60 text-sm mt-4 text-center font-productSans">
                  We review all applications carefully and will reach out in a few business days.
                </p>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default JobApplication;
